import { useContentLink, useContentSingletonFallback, useXcoreCore } from "@robe/client";
import { Box, Button, Flex, Link, Typography, useModal } from "@xcorejs/ui";
import RobeLoader from "../RobeLoader";
import { FormControl, Input } from "design-system/xcore/Form";
import { Field, FieldProps, Form, Formik, FormikHelpers } from "formik";
import { useState } from "react";
import { useLayout } from "xcore";
import { ForgottenPassPage, NewPassPage } from "xcore/types";
import { object, string } from "yup";
import { useRouter } from "next/router";
import { LostPasswordParams, sentryLogger, WebLostPasswordError, WebLostPasswordErrorType } from "@robe/core";
import { ErrorAnimatedIcon, SuccessAnimatedIcon } from "../Icons/AnimatedIcon";
import { UserLoginModal } from "./UserLoginModal";
import { useUser } from "hooks";

const resetPasswordSchema = object().shape({
  email: string().email().required(),
});

interface LostPasswordComponentProps {
  forgottenPassPage?: ForgottenPassPage;
  newPassPage?: NewPassPage;
}

const LostPasswordComponent = ({ forgottenPassPage: ssrForgottenPassPage, newPassPage: ssrNewPassPage }: LostPasswordComponentProps) => {
  const core = useXcoreCore();
  const { locale, pathname } = useRouter();
  const { stringify, general } = useLayout();
  const [userLogin] = useModal(UserLoginModal);
  const [close] = useModal();
  const [lostPasswordStatus, setLostPasswordStatus] = useState<"success" | "error" | null>(null);
  const { as } = useContentLink();
  const user = useUser();

  const { data: forgottenPassPage, isLoading: forgottenPassPageIsLoading } = useContentSingletonFallback<ForgottenPassPage>(
    "forgottenPassPage",
    ssrForgottenPassPage,
    {
      includeValues: true,
    }
  );
  const { data: newPassPage, isLoading: newPassPageIsLoading } = useContentSingletonFallback<NewPassPage>("newPassPage", ssrNewPassPage);

  const resetPassword = async (
    formValues: Pick<LostPasswordParams, "email">,
    helpers: FormikHelpers<Pick<LostPasswordParams, "email">>
  ) => {
    if (newPassPage) {
      const newPassPageUrl = as(newPassPage, { locale });

      if (newPassPageUrl) {
        try {
          const res = await core.web.lostPassword({
            ...formValues,
            locale: locale ?? "en",
            newPassPageUrl,
          });

          helpers.setSubmitting(false);

          if (res?.ok) {
            setLostPasswordStatus("success");

            return;
          }

          helpers.setFieldError("email", "Unknown error has occurred. Try again later.");
        } catch (error) {
          if (error instanceof WebLostPasswordError && error.detail === WebLostPasswordErrorType.MissingEmail) {
            helpers.setFieldError("email", stringify(general.values.emailNotFound));

            return;
          }

          sentryLogger({
            error,
            message: "Unexpected error occurred while resetting new password",
            context: {
              user,
            },
          });

          helpers.setFieldError("email", "Unknown error has occurred. Try again later.");
        }
      }
    }
  };

  return forgottenPassPageIsLoading || newPassPageIsLoading ? (
    <Flex justifyContent="center" alignItems="center" width="100%">
      <RobeLoader />
    </Flex>
  ) : forgottenPassPage ? (
    <>
      <Typography variant="h3" as="div" textAlign="center">
        {stringify(forgottenPassPage.values.forgotPassHeading)}
      </Typography>

      {stringify(forgottenPassPage.values.forgotPassIntro) && (
        <Typography mt="1rem" textAlign="center">
          {stringify(forgottenPassPage.values.forgotPassIntro)}
        </Typography>
      )}

      <Box width="100%" maxWidth="41rem" mx="auto" mt="3rem">
        {lostPasswordStatus === "success" ? (
          <Flex flexDirection="column" alignItems="center">
            <SuccessAnimatedIcon />

            <Typography mt="2rem" textAlign="center">
              {stringify(forgottenPassPage.values.successMsg)}
            </Typography>
          </Flex>
        ) : lostPasswordStatus === "error" ? (
          <Flex flexDirection="column" alignItems="center">
            <ErrorAnimatedIcon />

            <Typography mt="2rem" textAlign="center">
              Unexpected error occurred, try again later.
            </Typography>
          </Flex>
        ) : (
          <Formik initialValues={{ email: "" }} onSubmit={resetPassword} validationSchema={resetPasswordSchema}>
            <Form>
              <Field name="email">
                {({ field, meta }: FieldProps) => (
                  <FormControl
                    labelText={stringify(forgottenPassPage.values.forgotPassLabel)}
                    context="isRequired"
                    errorContext={meta.error && meta.touched ? "isInvalid" : "normal"}
                    width="100%"
                  >
                    <Input inputSize="md" width="100%" {...field} />
                  </FormControl>
                )}
              </Field>

              <Flex mt="2rem" justifyContent="center">
                <Link
                  justifyContent="center"
                  variant="underline"
                  onClick={() => {
                    close();
                    if (pathname !== "/user/login") {
                      // If already on login page, do not open login modal
                      userLogin();
                    }
                  }}
                >
                  {stringify(forgottenPassPage.values.forgotPassBackLink)}
                </Link>
              </Flex>

              <Flex justifyContent="center" mt={{ _: "2rem", sm: "3rem" }}>
                <Button>{stringify(forgottenPassPage.values.forgotPassResetButton)}</Button>
              </Flex>
            </Form>
          </Formik>
        )}
      </Box>
    </>
  ) : null;
};

export default LostPasswordComponent;
